/**
 * Per-thread state of a FINN-side interview cancel, plus the queueing itself.
 *
 * A hook rather than more lines in RequestsTab (that file sits at its 500-line ceiling), and
 * because the mapping from action outcomes to button states is a self-contained piece the
 * SchedulingBox depends on: 'cancelled' and 'already_cancelled' are terminal — the button becomes
 * a statement — while a failure returns to 'idle' so the recruiter can try again.
 */
import { useState } from 'react';
import { cancelInterviewAction } from './replyAction';
import type { InboundRequest } from '../../types/taInboundRequests';
import type { PendingAction } from './usePendingActions';

export type CancelState = 'idle' | 'pending' | 'cancelled' | 'already_cancelled';

export function useCancelInterview(
  deferAction: (a: Omit<PendingAction, 'secondsLeft'>) => void
) {
  const [states, setStates] = useState<Record<string, CancelState>>({});

  const queueCancelInterview = (item: InboundRequest, bookingRef: string) => {
    setStates((m) => ({ ...m, [item.threadId]: 'pending' }));
    deferAction(
      cancelInterviewAction({
        item,
        bookingRef,
        onDone: (outcome) =>
          setStates((m) => ({
            ...m,
            [item.threadId]:
              outcome === 'cancelled'
                ? 'cancelled'
                : outcome === 'already_cancelled'
                  ? 'already_cancelled'
                  : 'idle',
          })),
        onStopped: () => setStates((m) => ({ ...m, [item.threadId]: 'idle' })),
      })
    );
  };

  return { cancelStates: states, queueCancelInterview };
}
